/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute } from '@angular/router';
import { BaseComponent } from '../../../../../core/components';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HostnameApiService } from '../../../shared/http';
import { LbErrorService } from '../../../shared/services/components';
import { LbRbacService } from '../../../shared/services/utilities';
import { LogService, UtilsService, ValidationService } from '../../../../../core/services';
import { Observable } from 'rxjs';
import { domainValidation, hostnameValidation } from '../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-hostname-dns',
    templateUrl: './hostnameDns.component.html',
    styleUrls: ['./hostnameDns.component.scss']
})

export class HostnameDnsComponent extends BaseComponent implements OnInit {
    /**
     * To store the host name & DNS from.
     */
    public hostnameDnsForm: FormGroup;

    /**
     * To store the initial data when the page first loads.
     * @private
     */
    private initialData: any = {};

    constructor(
        public rbacService: LbRbacService,
        private formBuilder: FormBuilder,
        private hostnameDnsApi: HostnameApiService,
        private lbErrorService: LbErrorService,
        private logService: LogService,
        private route: ActivatedRoute,
        private utilsService: UtilsService
    ) {
        super();

        this.setUpForm();
        // Set local variables from resolver
        this.setFormValues(this.route.snapshot.data['hostnameDns']);
    }

    /**
     * ngOnInit()
     */
    public ngOnInit(): void {
        this.subscribeToFormChanges();
    }

    /**
     * Post time zone to server.
     */
    public submitHostnameDns(): Observable<any> {
        return new Observable((observer) => {
            const data = JSON.stringify(this.getFormValues());
            this.hostnameDnsApi.addHostname(data)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: response => {
                        this.setFormValues(response);
                    },
                    error: error => {
                        this.lbErrorService.pageWithErrors(this.hostnameDnsForm, error.error.errors);
                        this.logService.error('HostnameDnsComponent: submitHostnameDns', 'Error submitting data', error);
                    },
                    complete: () => {
                        observer.next();
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Set value in the form.
     * @param data
     * @private
     */
    private setFormValues(data: any): void {
        this.hostnameDnsForm.reset();
        this.hostnameDnsForm.controls['hostname'].setValue(data['hostname']);
        this.hostnameDnsForm.controls['domain'].setValue(data['domain']);
        this.hostnameDnsForm.controls['dns0'].setValue(data['dns'][0]);
        this.hostnameDnsForm.controls['dns1'].setValue(data['dns'][1]);
        this.hostnameDnsForm.controls['dns2'].setValue(data['dns'][2]);

        this.initialData = this.hostnameDnsForm.value;
    }

    /**
     * Get values from the form.
     * @returns {{hostname: string, domain: string, dns: any[]}}
     * @private
     */
    private getFormValues(): any {
        const dns = [];
        dns.push(this.hostnameDnsForm.value.dns0);
        dns.push(this.hostnameDnsForm.value.dns1);
        dns.push(this.hostnameDnsForm.value.dns2);

        return {
            hostname: this.hostnameDnsForm.value.hostname,
            domain: this.hostnameDnsForm.value.domain,
            dns: dns
        };
    }

    /**
     * Set up the form.
     * @private
     */
    private setUpForm(): void {
        this.hostnameDnsForm = this.formBuilder.group({
            hostname: ['', [Validators.required, hostnameValidation, Validators.maxLength(63)]],
            domain: ['', [domainValidation, Validators.maxLength(253)]],
            dns0: ['', ValidationService.ipDomainValidator],
            dns1: ['', ValidationService.ipDomainValidator],
            dns2: ['', ValidationService.ipDomainValidator]
        });
    }

    /**
     * Subscribe to the default gateway form changes.
     * @private
     */
    private subscribeToFormChanges(): void {
        this.hostnameDnsForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: form => {
                    this.utilsService.debounce('form', () => {
                        if (!this.utilsService.hasFormDataChanged(this.initialData, form)) {
                            this.hostnameDnsForm.markAsPristine();
                        }
                    });
                }
            });
    }
}
